import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Theme model for aigen Studio.
 *
 * design.md ships TWO skins: light (warm cream) and ink (warm dark, §2.5).
 * The user wants a *proper* app-wide dark toggle PLUS two sanctioned dark
 * "moments": Present mode and Night Today. So:
 *
 *   mode: "light" | "ink" | "auto"   — the user's preference (persisted)
 *   - "auto" resolves to ink when the OS prefers dark OR it's night (Night Today)
 *   present: boolean                  — Present mode forces ink while active
 *
 * `resolved` is the actual skin applied (the `.dark` class on <html>).
 */
export type ThemeMode = "light" | "ink" | "auto";

interface ThemeState {
  mode: ThemeMode;
  resolved: "light" | "ink";
  present: boolean;
  setMode: (m: ThemeMode) => void;
  toggle: () => void;
  setPresent: (p: boolean) => void;
}

const ThemeContext = createContext<ThemeState | null>(null);
const STORAGE_KEY = "aigen-studio-theme";

function isNight(): boolean {
  const h = new Date().getHours();
  return h >= 20 || h < 6;
}

function prefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: dark)").matches
  );
}

function resolve(mode: ThemeMode, present: boolean): "light" | "ink" {
  if (present) return "ink";
  if (mode === "auto") return prefersDark() || isNight() ? "ink" : "light";
  return mode;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const saved = (typeof localStorage !== "undefined" &&
      localStorage.getItem(STORAGE_KEY)) as ThemeMode | null;
    return saved ?? "light";
  });
  const [present, setPresent] = useState(false);
  const [resolved, setResolved] = useState<"light" | "ink">(() =>
    resolve(mode, false),
  );

  // Re-resolve whenever inputs change, and react to OS + clock for "auto".
  useEffect(() => {
    const apply = () => setResolved(resolve(mode, present));
    apply();
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener?.("change", apply);
    const clock = window.setInterval(apply, 60_000); // catch Night Today rollover
    return () => {
      mq.removeEventListener?.("change", apply);
      window.clearInterval(clock);
    };
  }, [mode, present]);

  // Drive the single source of truth: the `.dark` class on <html>.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", resolved === "ink");
    root.style.colorScheme = resolved === "ink" ? "dark" : "light";
  }, [resolved]);

  const setMode = useCallback((m: ThemeMode) => {
    setModeState(m);
    try {
      localStorage.setItem(STORAGE_KEY, m);
    } catch {
      /* ignore */
    }
  }, []);

  const toggle = useCallback(() => {
    setMode(resolved === "ink" ? "light" : "ink");
  }, [resolved, setMode]);

  const value = useMemo<ThemeState>(
    () => ({ mode, resolved, present, setMode, toggle, setPresent }),
    [mode, resolved, present, setMode, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
